import {
  Component,
  computed,
  input,
  linkedSignal,
  output,
  viewChildren
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { printFilter } from '@core/filters/path-filter/dsl/printer';
import {
  TemplateValues,
  fillTemplate,
  templateInputs,
  templateIsUsable
} from '@core/filters/path-filter/templates';
import { FilterVocabulary } from '@core/filters/path-filter/vocabulary/vocabulary';
import { FilterPresetModel } from '@core/models/filter-preset.model';
import { ICONS } from '@core/services/icons/icons.service';

import { DslTextComponent } from '@shared/components/path-filter/dsl-text/dsl-text.component';
import {
  TemplateValue,
  TemplateValueInputComponent
} from '@shared/components/path-filter/template-value-input/template-value-input.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';

@Component({
  selector: 'app-filter-template-activation',
  imports: [
    TranslatePipe,
    DslTextComponent,
    TemplateValueInputComponent,
    FocusFirstDirective
  ],
  templateUrl: './filter-template-activation.component.html',
  styles: ``
})
export class FilterTemplateActivationComponent {
  readonly preset = input.required<FilterPresetModel>();
  readonly vocabulary = input<FilterVocabulary | null>(null);
  /** Values of the activation being changed, in their wire form; empty on a first one. */
  readonly initialValues = input<TemplateValues>({});

  private readonly fields = viewChildren(TemplateValueInputComponent);

  readonly confirmed = output<TemplateValues>();
  readonly cancelled = output<void>();

  protected readonly ICONS = ICONS;

  /** gbe stores template trees unchecked: one the printer cannot walk is shown as stored. */
  protected readonly filterText = computed(() => {
    const filter = this.preset().pathFilter;
    if (!filter) {
      return '';
    }
    try {
      return printFilter(filter);
    } catch (error) {
      if (error instanceof TypeError) {
        return JSON.stringify(filter);
      }
      throw error;
    }
  });

  protected readonly inputs = computed(() =>
    templateInputs(
      this.preset().pathFilter,
      this.preset().parameters ?? [],
      this.vocabulary()
    )
  );

  protected readonly values = linkedSignal<TemplateValues>(() => ({
    ...this.initialValues()
  }));

  /** A template stored by another client may hold a hole no value can fill; it is never applied. */
  protected readonly usable = computed(() => templateIsUsable(this.preset().pathFilter));

  protected readonly canConfirm = computed(
    () =>
      fillTemplate(
        this.preset().pathFilter,
        this.preset().parameters ?? [],
        this.values()
      ) !== null
  );

  protected inputId(name: string): string {
    return `template-${name}`;
  }

  protected setValue(name: string, value: TemplateValue): void {
    this.values.update((values) => ({ ...values, [name]: value }));
  }

  /**
   * Enter applies from any field, an open entity list included: its rows are picked by click
   * only, so Enter there has nothing else to do. A picker being searched keeps Enter, so a typed
   * term is not applied half-done. Buttons and selects keep their own. A single-value picker's
   * chip is where the focus lands after a pick, so Enter there applies too.
   */
  protected onEnter(event: Event): void {
    const target = event.target;
    const onField =
      target instanceof HTMLInputElement ||
      this.fields().some((field) => field.isValueChip(target));
    if (!onField || (event as KeyboardEvent).isComposing) return;
    if (this.fields().some((field) => field.searching())) return;
    // A duration commits on `change`, which the browser fires after this keydown: commit it first.
    if (target instanceof HTMLInputElement) target.dispatchEvent(new Event('change'));
    this.confirm();
  }

  protected confirm(): void {
    if (this.canConfirm()) {
      this.confirmed.emit(this.values());
    }
  }
}
